/**
 * The REST client. Four calls, all of them about sessions, none of them carrying a coordinate.
 */

import type {
  ApiErrorCode,
  CreateSessionRequest,
  CreateSessionResponse,
  ExtendSessionResponse,
  JoinSessionResponse,
  SessionStatusResponse,
} from '@beeline/shared';

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when retrying is pointless because the session is simply over. */
  get isTerminal(): boolean {
    return (
      this.code === 'session_not_found' ||
      this.code === 'session_expired' ||
      this.code === 'session_full'
    );
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      /*
       * Only announce a JSON body when there is one.
       *
       * `POST /extend` carries nothing — the code is in the path. Declaring `application/json`
       * anyway makes Fastify run its JSON parser over an empty body and reject the request with
       * a 400 before the route is ever reached, which is how "Add an hour" spent its whole life
       * returning "Could not extend".
       */
      headers:
        init?.body === undefined
          ? init?.headers
          : { 'content-type': 'application/json', ...init?.headers },
    });
  } catch {
    // Offline, captive portal, server down — all the same to the caller.
    throw new ApiError('internal', 'Cannot reach Beeline. Check your connection.', 0);
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: ApiErrorCode;
      message?: string;
    } | null;
    throw new ApiError(
      body?.error ?? 'internal',
      body?.message ?? 'Something went wrong.',
      response.status,
    );
  }

  return (await response.json()) as T;
}

export const api = {
  createSession: (options: CreateSessionRequest = {}): Promise<CreateSessionResponse> =>
    request('/api/sessions', { method: 'POST', body: JSON.stringify(options) }),

  getSession: (code: string): Promise<SessionStatusResponse> =>
    request(`/api/sessions/${encodeURIComponent(code)}`),

  joinSession: (code: string, name: string | null): Promise<JoinSessionResponse> =>
    request(`/api/sessions/${encodeURIComponent(code)}/join`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  extendSession: (code: string): Promise<ExtendSessionResponse> =>
    request(`/api/sessions/${encodeURIComponent(code)}/extend`, { method: 'POST' }),
};
